import React from 'react';
import { Table as TanstackTable } from '@tanstack/react-table';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { TableData, RowActionRenderer, getTableFilterMeta, getTableMeta } from './types';
import { STICKY_TABLE_FILTER_ROW_TOP } from './constants';
import { GENERIC_ELEMENT_ID, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';
import {
	getPinnedColumnInfo,
	getPinnedColumnStyle,
	LEADING_TRACK_WIDTH,
} from './utils/pinnedColumn';

interface Props<T extends TableData> {
	table: TanstackTable<T>;
	enableRowDragAndDrop?: boolean;
	enableRowSelection?: boolean;
	showRowSelection?: boolean;
	pinSelectionColumn?: boolean;
	enableRowActions?: boolean;
	renderRowActions?: RowActionRenderer<T>;
	renderRowActionMenuItems?: RowActionRenderer<T>;
	minWidth: number;
	enableStickyHeader?: boolean;
}

export const MantineTableFilterRow = <T extends TableData>({
	table,
	enableRowDragAndDrop,
	enableRowSelection,
	showRowSelection,
	pinSelectionColumn = false,
	enableRowActions,
	renderRowActions,
	renderRowActionMenuItems,
	enableStickyHeader,
}: Props<T>) => {
	const showFilters = getTableFilterMeta(table).showColumnFilters;
	const { pinnedColumns, lastPinnedColumnId } = getTableMeta(table);
	const isPinnedMode = !!pinnedColumns && Object.keys(pinnedColumns).length > 0;
	const stickyOnCell = enableStickyHeader && !isPinnedMode;

	if (!showFilters) {
		return null;
	}

	let gridColumnIndex = 1;

	const filterCells = (
		<>
			{enableRowDragAndDrop && (
				<div
					key="filter-drag-handle"
					style={{
						gridColumn: gridColumnIndex++,
						padding: '4px 8px',
						borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						...(stickyOnCell && {
							position: 'sticky',
							top: STICKY_TABLE_FILTER_ROW_TOP,
							backgroundColor: 'white',
							zIndex: Z_INDEX.STICKY_HEADER,
						}),
					}}
				/>
			)}
			{enableRowSelection && showRowSelection && (
				<div
					key="filter-select"
					style={{
						gridColumn: gridColumnIndex++,
						padding: '4px 8px',
						borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						...(stickyOnCell && {
							position: 'sticky',
							top: STICKY_TABLE_FILTER_ROW_TOP,
							backgroundColor: 'white',
							zIndex: Z_INDEX.STICKY_HEADER,
						}),
						...(pinSelectionColumn &&
							getPinnedColumnStyle(true, Z_INDEX.STICKY_PINNED_HEADER, {
								backgroundColor: 'white',
								left: enableRowDragAndDrop ? LEADING_TRACK_WIDTH : 0,
								withShadow: false,
							})),
					}}
				/>
			)}
			{table.getHeaderGroups().map((headerGroup) =>
				headerGroup.headers.map((header) => {
					const hasSubHeaders = header.subHeaders && header.subHeaders.length > 0;

					// Skip parent headers - they don't correspond to actual grid columns
					if (hasSubHeaders) {
						return null;
					}

					if (header.isPlaceholder) {
						return <div key={header.id} style={{ gridColumn: gridColumnIndex++ }} />;
					}
					const column = header.column;
					const colDef = column.columnDef as ExtendedColumnDef<T>;
					const pin = getPinnedColumnInfo(pinnedColumns, lastPinnedColumnId, column.id);

					if (column.getIsVisible() && colDef.showFilter === false) {
						return (
							<div
								key={`${header.id}-no-filter`}
								style={{
									gridColumn: gridColumnIndex++,
									width: '100%',
									borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									...(stickyOnCell && {
										position: 'sticky',
										top: STICKY_TABLE_FILTER_ROW_TOP,
										backgroundColor: 'white',
										zIndex: Z_INDEX.STICKY_HEADER,
									}),
									...getPinnedColumnStyle(
										pin.isPinned,
										Z_INDEX.STICKY_PINNED_HEADER,
										{
											backgroundColor: 'white',
											left: pin.left,
											withShadow: pin.isLast,
										}
									),
								}}
							/>
						);
					}

					// Check if there are any parent columns (columns with subHeaders)
					const hasParentColumns = table
						.getHeaderGroups()
						.some((headerGroup) =>
							headerGroup.headers.some(
								(header) => header.subHeaders && header.subHeaders.length > 0
							)
						);

					let isLastChildHeader = false;
					if (hasParentColumns) {
						for (const parentHeader of table.getHeaderGroups()[0].headers) {
							if (
								parentHeader.subHeaders &&
								parentHeader.subHeaders.length > 0 &&
								parentHeader.subHeaders.some(
									(subHeader) => subHeader.id === header.id
								)
							) {
								const lastChildId =
									parentHeader.subHeaders[parentHeader.subHeaders.length - 1].id;
								isLastChildHeader = header.id === lastChildId;
								break;
							}
						}
					}

					const isLastColumn =
						headerGroup.headers[headerGroup.headers.length - 1].id === header.id;
					const CustomFilterComponent = colDef.filter;

					const currentGridColumn = gridColumnIndex;
					gridColumnIndex++;

					return (
						<div
							key={`${header.id}-filter`}
							data-testid={makeComplexDataTestId(
								GENERIC_ELEMENT_ID.MANTINE_TABLE_COLUMN_FILTER,
								column.id
							)}
							style={{
								gridColumn: currentGridColumn,
								padding: '4px 8px',
								position: stickyOnCell ? 'sticky' : 'relative',
								overflow: 'visible',
								borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								...(stickyOnCell && {
									top: STICKY_TABLE_FILTER_ROW_TOP,
									backgroundColor: 'white',
									zIndex: Z_INDEX.STICKY_HEADER,
								}),
								...(hasParentColumns &&
									isLastChildHeader &&
									!isLastColumn && {
										borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									}),
								...getPinnedColumnStyle(
									pin.isPinned,
									Z_INDEX.STICKY_PINNED_HEADER,
									{
										backgroundColor: 'white',
										left: pin.left,
										withShadow: pin.isLast,
									}
								),
							}}
						>
							{typeof CustomFilterComponent === 'function'
								? (
										CustomFilterComponent as (props: {
											column: typeof header.column;
										}) => React.ReactNode
									)({
										column: header.column,
									})
								: null}
						</div>
					);
				})
			)}
			{enableRowActions && (renderRowActions || renderRowActionMenuItems) ? (
				<div
					key="filter-actions"
					style={{
						gridColumn: gridColumnIndex,
						padding: '4px 8px',
						borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						...(stickyOnCell && {
							position: 'sticky',
							top: STICKY_TABLE_FILTER_ROW_TOP,
							backgroundColor: 'white',
							zIndex: Z_INDEX.STICKY_HEADER,
						}),
					}}
				/>
			) : null}
		</>
	);

	if (isPinnedMode) {
		return (
			<div
				style={{
					gridColumn: '1 / -1',
					display: 'grid',
					gridTemplateColumns: getTableMeta(table).gridLayout?.gridTemplateColumns,
					backgroundColor: 'white',
					...(enableStickyHeader && {
						position: 'sticky',
						top: STICKY_TABLE_FILTER_ROW_TOP,
						zIndex: Z_INDEX.STICKY_HEADER,
					}),
				}}
			>
				{filterCells}
			</div>
		);
	}

	return filterCells;
};
